/**
 * O que o marketplace cobra por uma venda.
 *
 * Este módulo já errou duas vezes, e cada correção veio de olhar o dado real:
 *
 * 1. Uma taxa única por perfil tratava eletrônico e vestuário como se
 *    custassem o mesmo.
 * 2. Uma taxa por CATEGORIA parecia resolver — e não resolvia. Medindo pela
 *    API do Mercado Livre, a comissão depende **também do preço**. Em
 *    Acessórios para Veículos, o Premium cobra 17% até R$ 100, 14% entre
 *    R$ 150 e R$ 500, e volta a 17% acima disso.
 *
 * Guardar um percentual por categoria daria um número certo numa faixa e errado
 * nas outras — do jeito mais perigoso, porque pareceria conferido. Num item de
 * R$ 500 no Premium, três pontos percentuais são R$ 15, que costuma ser a
 * margem inteira.
 *
 * Por isso a tarifa **não se cadastra: se consulta**, para a categoria e o preço
 * exatos da análise. O que sobra de puro aqui é decidir se dá para perguntar, e
 * o que fazer quando a resposta não vem.
 *
 * A regra de sempre continua valendo: **ausente nunca vira zero.** Sem resposta,
 * a análise sai como DADOS_INSUFICIENTES dizendo o que falta.
 */

/** Os dois tipos de anúncio que o Mercado Livre mantém vivos. */
export type TipoAnuncio = 'gold_special' | 'gold_pro';

export const ROTULO_TIPO_ANUNCIO: Record<TipoAnuncio, string> = {
  gold_special: 'Clássico',
  gold_pro: 'Premium',
};

export const TIPOS_ANUNCIO: readonly TipoAnuncio[] = ['gold_special', 'gold_pro'];

/**
 * O que o marketplace cobra NESTA venda. Veio de consulta, para um preço
 * específico — não é atributo da categoria.
 */
export interface TarifaMarketplace {
  percentualBps: number;
  fixoCents: number;
}

export type MotivoSemTarifa =
  /** O produto não tem categoria: resolve-se na ficha da mercadoria. */
  | 'SEM_CATEGORIA'
  /** Categoria local, sem vínculo com o Mercado Livre: resolve-se em Configurações. */
  | 'CATEGORIA_SEM_VINCULO'
  /** A consulta não foi feita, ou falhou: tentar de novo. */
  | 'NAO_CONSULTADA';

export type TarifaResolvida =
  | { definida: true; tarifa: TarifaMarketplace }
  | { definida: false; motivo: MotivoSemTarifa };

export const RECADO_SEM_TARIFA: Record<MotivoSemTarifa, string> = {
  SEM_CATEGORIA: 'Este produto não tem categoria. Escolha uma na ficha da mercadoria.',
  CATEGORIA_SEM_VINCULO:
    'Esta categoria não veio do Mercado Livre, então não dá para consultar a tarifa dela. Sincronize as categorias em Configurações.',
  NAO_CONSULTADA: 'A tarifa do Mercado Livre ainda não foi consultada para este preço.',
};

/**
 * Decide se a tarifa consultada pode ser usada — e por que não, quando não pode.
 *
 * Puro de propósito: a consulta é assíncrona e mora fora. Aqui fica só a regra
 * que separa "não deu para perguntar" de "perguntei e não veio", porque a ação
 * de quem lê é diferente em cada caso.
 */
export function resolverTarifa(
  categoria: { mlCategoriaId?: string | null } | null | undefined,
  consultada: TarifaMarketplace | null | undefined,
): TarifaResolvida {
  if (!categoria) return { definida: false, motivo: 'SEM_CATEGORIA' };
  if (!categoria.mlCategoriaId) return { definida: false, motivo: 'CATEGORIA_SEM_VINCULO' };
  if (!consultada) return { definida: false, motivo: 'NAO_CONSULTADA' };

  const { percentualBps, fixoCents } = consultada;
  if (!Number.isInteger(percentualBps) || !Number.isInteger(fixoCents)) {
    // Valor fracionário aqui significa conversão errada na fronteira. Recusar
    // é melhor que arredondar em silêncio: o erro fica visível.
    return { definida: false, motivo: 'NAO_CONSULTADA' };
  }
  if (percentualBps < 0 || fixoCents < 0) return { definida: false, motivo: 'NAO_CONSULTADA' };

  return { definida: true, tarifa: { percentualBps, fixoCents } };
}
